import React, { FC, useState, createContext } from "react";
import { graphql, PageProps, useStaticQuery } from "gatsby";
import classNames from "classnames";
import Navbar from "../../components/Navbar";
import Search from "../../components/Search";
import { Link } from "kaleidoscope/src";
import { kebabCase } from "lodash";
import "../../index.less";
import { Helmet } from "react-helmet";

export interface IMainContext {
  category: string;
  pathname: string;
  sidebarOpen: boolean;
  setSidebarOpen: (isOpen: boolean) => void;
  hasSidebar: boolean;
  setHasSidebar: (hasSidebar: boolean) => void;
}

export const MainContext = createContext({} as IMainContext);

const Main: FC<PageProps> = ({ children, location }) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hasSidebar, setHasSidebar] = useState(false);
  const { pathname } = location;

  const data = useStaticQuery(graphql`
    query MainLayoutQuery {
      allNavbarYaml {
        nodes {
          label
        }
      }
      allSidebarYaml {
        nodes {
          label
          categories {
            label
            pages
          }
        }
      }
      metadataYaml {
        description
        title
      }
    }
  `);

  const category = pathname.split("/").filter(Boolean)[0];

  const showSidebar = data.allNavbarYaml.nodes
    .map(({ label }: { label: string }) => kebabCase(label))
    .includes(category);
  const mainId = "kld-main-content";

  return (
    <MainContext.Provider value={{ category, pathname, sidebarOpen, setSidebarOpen, hasSidebar, setHasSidebar }}>
      <Helmet>
        <title>{data.metadataYaml.title}</title>
        <meta name="description" content={data.metadataYaml.description} />
      </Helmet>
      <div className="kld-main">
        <Link className="kld-main__skip-link" href={`#${mainId}`}>
          Skip to main content
        </Link>
        <Navbar toggleSearch={() => setSearchOpen(!searchOpen)} />
        <Search open={searchOpen} setOpen={setSearchOpen} />
        <main
          id={mainId}
          tabIndex={-1}
          className={classNames("kld-main__content", { "kld-main__content--full": !showSidebar })}
        >
          {children}
        </main>
      </div>
    </MainContext.Provider>
  );
};

export default Main;
